import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';

import { SkeletonTable } from '../SpotlightsSkeleton';
import { getResolvedTriage } from '../../../api/spotlights';

// The history side of the triage queue: cleared issues, most-recent first, with the admin who
// resolved each one. Auto-resolutions (pixel connected, identity unpaused, …) carry no admin, so
// the "Resolved by" column falls back to a humanized signal.

// Fallback for digest items cached before the server started sending a platform-aware adUrl.
const metaAdUrl = adId => (adId ? `https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=${adId}` : null);

// Humanized resolvedByType for the auto-resolutions that have no admin actor.
const RESOLVED_BY_LABELS = {
  admin_resolved: 'Admin',
  admin_dismissed: 'Admin · dismissed',
  pixel_connected: 'Auto · pixel connected',
  identity_unpaused: 'Auto · identity unpaused',
  started_spending: 'Auto · started spending',
  admin_reconnect: 'Manual reconnect',
  admin_sync: 'Synced to Meta',
  exclusion_attached: 'Auto · exclusion attached'
};

const resolvedByLabel = item => item.resolvedByAdminName || RESOLVED_BY_LABELS[item.resolvedByType] || item.resolvedByType || '—';

const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

const SpotlightsResolved = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getResolvedTriage({ page });
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load resolved items.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.ceil(total / 50);

  return (
    <div className='spotlights-error'>
      <div className='sl-controls'>
        <span style={{ color: '#767676', fontSize: 13 }}>{total} resolved</span>
        <button className='sl-btn secondary small' style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className='sl-error'>{error}</div>}

      {loading && !items.length ? (
        <SkeletonTable rows={8} cols={6} />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <table className='sl-table'>
            <thead>
              <tr>
                <th>Spotlight</th>
                <th>Brand × Creator</th>
                <th>Reason</th>
                <th className='no-sort'>Resolved by</th>
                <th className='no-sort'>Resolved</th>
                <th className='no-sort' style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className='sl-empty'>
                    Nothing has been resolved yet.
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <tr key={item.eventId}>
                    <td>{item.Spotlight_id ? `#${item.Spotlight_id}` : item.Brand_id ? `Brand ${item.Brand_id}` : '—'}</td>
                    <td>
                      {item.brandName || '?'} {item.creatorName ? `× ${item.creatorName}` : ''}
                    </td>
                    <td>
                      <span className='sl-error-type'>{item.type}</span>
                      {item.reason ? <span className='sl-error-reason'> — {item.reason}</span> : null}
                    </td>
                    <td>{resolvedByLabel(item)}</td>
                    <td>{fmtDate(item.resolvedAt)}</td>
                    <td className='sl-error-actions'>
                      {(item.adUrl || metaAdUrl(item.metaAdId)) && (
                        <button
                          className='sl-btn secondary small'
                          onClick={() => window.open(item.adUrl || metaAdUrl(item.metaAdId), '_blank', 'noopener')}
                        >
                          {item.adPlatform === 'tiktok' ? 'TikTok' : 'Meta'}
                        </button>
                      )}
                      {item.Spotlight_id && (
                        <button className='sl-btn secondary small' onClick={() => navigator.clipboard?.writeText(String(item.Spotlight_id))}>
                          Copy ID
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pages > 1 && (
            <div className='sl-controls' style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className='sl-btn secondary small' disabled={page === 0} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span style={{ fontSize: 13 }}>
                Page {page + 1} of {pages}
              </span>
              <button className='sl-btn secondary small' disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpotlightsResolved;
